import type React from 'react';
import { useTranslation } from 'react-i18next';

import ArrowSoftRight from '@/assets/icons/arrow_soft_right.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import styles from './ImageNavButtons.module.scss';

export interface ImageNavButtonsProps {
    onPrevious: () => void;
    onNext: () => void;
    children?: React.ReactNode;
    nextDisabled?: boolean;
    previousDisabled?: boolean;
}

/**
 * The previous/next chevrons either side of the image.
 *
 * The direction used to make a round trip through the DOM: both buttons shared one handler
 * and carried `value="previous"`/`value="next"`, which the parent read back with
 * `event.currentTarget.value.toLowerCase()`. The stylesheet also selected on that attribute
 * to flip the previous chevron 180 degrees, so the round trip could not simply be deleted -
 * that rotation is now a class.
 *
 * The buttons also had `tabIndex={2}` and `tabIndex={1}`: positive tab indices, in reverse
 * visual order, which pulled them to the front of the whole page's tab order. Removed - DOM
 * order is already correct.
 */
export default function ImageNavButtons(props: ImageNavButtonsProps) {
    const { children, onPrevious, onNext, nextDisabled = false, previousDisabled = false } = props;

    const { t } = useTranslation();

    return (
        <div className={styles.imageNavButtons}>
            <button
                type="button"
                onClick={onPrevious}
                className={`${styles.control_button} ${styles.previous}`}
                aria-label={t('organisms.imageViewer.previousImage')}
                name="previousImage"
                disabled={previousDisabled}>
                <SvgIcon icon={ArrowSoftRight} size="small" />
            </button>
            <div>{children}</div>
            <button
                type="button"
                onClick={onNext}
                className={styles.control_button}
                aria-label={t('organisms.imageViewer.nextImage')}
                name="nextImage"
                disabled={nextDisabled}>
                <SvgIcon icon={ArrowSoftRight} size="small" />
            </button>
        </div>
    );
}
